import {
  Autocomplete,
  DataGrid,
  Form,
  StatusBadge,
  useNotifications
} from '@ijoin/components';
import {
  useDeleteAdminAdvisoryOrganizationRecordKeepingOrganization,
  useGetAdminAdvisoryOrganization,
  useGetAdminAdvisoryOrganizationRecordKeepingOrganizations,
  useGetAdminAdvisoryOrganizationUnlinkedRecordKeepingOrganizations,
  usePatchAdminAdvisoryOrganization,
  usePostAdminAdvisoryOrganizationRecordKeepingOrganization
} from '@ijoin/hooks';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { useForm } from 'react-hook-form';

import { LinkButton, OrgRkoActionsCell } from '../../../components';

const RecordKeepersTab = () => {
  const { advisoryOrganizationId } = Route.useParams();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const org = useGetAdminAdvisoryOrganization(advisoryOrganizationId);
  const billingRkoId = org.data?.billingRecordKeepingOrgId;

  const linked = useGetAdminAdvisoryOrganizationRecordKeepingOrganizations(
    advisoryOrganizationId
  );
  const unlinked =
    useGetAdminAdvisoryOrganizationUnlinkedRecordKeepingOrganizations(
      advisoryOrganizationId
    );

  type RkoOption = { label: string; value: string };

  const linkForm = useForm<{ rko: null | RkoOption }>({
    defaultValues: { rko: null }
  });
  const rkoToLink = linkForm.watch('rko');

  const invalidate = () => {
    queryClient.invalidateQueries({
      predicate: query =>
        query.queryKey.some(
          key =>
            typeof key === 'string' &&
            key.startsWith(
              `/api/v3/admin/advisory-organizations/${advisoryOrganizationId}`
            )
        )
    });
  };

  const linkRko = usePostAdminAdvisoryOrganizationRecordKeepingOrganization({
    mutation: {
      onError: () =>
        notifications.error('Failed to link record keeping organization'),
      onSuccess: () => {
        invalidate();
        linkForm.setValue('rko', null);
        notifications.success('Record keeping organization linked');
      }
    }
  });
  const unlinkRko = useDeleteAdminAdvisoryOrganizationRecordKeepingOrganization(
    {
      mutation: {
        onError: () =>
          notifications.error('Failed to unlink record keeping organization'),
        onSuccess: () => {
          invalidate();
          notifications.success('Record keeping organization unlinked');
        }
      }
    }
  );

  const setBillingRko = usePatchAdminAdvisoryOrganization({
    mutation: {
      onError: () => notifications.error('Failed to set billing record keeper'),
      onSuccess: () => {
        invalidate();
        notifications.success('Billing record keeper updated');
      }
    }
  });

  const columns: GridColDef[] = [
    {
      field: 'name',
      flex: 2,
      headerName: 'Name',
      renderCell: params => (
        <LinkButton
          data-testid={camelCase(params.row.name)}
          params={{
            recordKeepingOrganizationId: params.row.recordKeepingOrganizationId
          }}
          to='/recordkeepers/$recordKeepingOrganizationId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'city',
      flex: 1,
      headerName: 'City'
    },
    {
      field: 'state',
      flex: 0.75,
      headerName: 'State'
    },
    {
      align: 'center',
      field: 'billing',
      flex: 0.75,
      headerName: 'Billing',
      renderCell: params =>
        params.row.recordKeepingOrganizationId === billingRkoId ? (
          <StatusBadge severity='success'>Billing</StatusBadge>
        ) : null,
      sortable: false
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <OrgRkoActionsCell
          isBilling={params.row.recordKeepingOrganizationId === billingRkoId}
          name={params.row.name}
          onSetBilling={() =>
            setBillingRko.mutate({
              advisoryOrganizationId,
              data: {
                billingRecordKeepingOrgId:
                  params.row.recordKeepingOrganizationId
              }
            })
          }
          onUnlink={() =>
            unlinkRko.mutate({
              advisoryOrganizationId,
              recordKeepingOrganizationId:
                params.row.recordKeepingOrganizationId
            })
          }
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      <Stack alignItems='center' direction='row' spacing={0.5}>
        <Typography variant='h6'>
          Linked Record Keeping Organizations
        </Typography>
        <Tooltip title='Controls which recordkeepers this advisory organization can work with. Recordkeepers are the third-party systems of record that hold participant accounts and process plan transactions.'>
          <IconButton>
            <HelpOutlineIcon color='action' />
          </IconButton>
        </Tooltip>
      </Stack>
      {!billingRkoId && !!linked.data?.length && (
        <Typography color='text.secondary' variant='body2'>
          No billing record keeper set. Choose one from a row&apos;s actions
          below to enable ACT Analytics licensing.
        </Typography>
      )}
      <Form methods={linkForm}>
        <Stack alignItems='flex-end' direction='row' spacing={1} sx={{ mb: 2 }}>
          <Autocomplete<RkoOption>
            data-testid='rkoToLink'
            hideError
            label='Record Keeping Organization'
            name='rko'
            options={(unlinked.data ?? []).map(rko => ({
              label: rko.name,
              value: rko.recordKeepingOrganizationId
            }))}
            sx={{ minWidth: 300 }}
          />
          <Tooltip title='Link'>
            <IconButton
              data-testid='linkRko'
              disabled={!rkoToLink}
              onClick={() => {
                if (!rkoToLink) return;
                linkRko.mutate({
                  advisoryOrganizationId,
                  recordKeepingOrganizationId: rkoToLink.value
                });
              }}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Form>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.recordKeepingOrganizationId}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } }
        }}
        loading={linked.isFetching}
        pageSizeOptions={[5, 10, 25]}
        rows={linked.data ?? []}
        showColumnMenu
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'linkedRecordKeepingOrganizations'
          }
        }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Stack>
  );
};

export const Route = createFileRoute(
  '/organizations/$advisoryOrganizationId/record-keepers'
)({
  component: RecordKeepersTab
});
